import { useState, KeyboardEvent, useEffect, useRef, useCallback } from 'react';
import { MapboxMap, MapLocation, MapStyleKey, MAP_STYLES, getStaticMapPreviewUrl, getDefaultZoom } from './components/MapboxMap';
import { useRenderedMapPreview } from './components/useRenderedMapPreview';
import { renderMapImage } from './components/renderMapImage';
import { TEST_MODE, apiUrl } from './testMode';
import svgPaths from '../imports/MapDesktopDefault/svg-dz48wvnk9k';
import mapboxgl from 'mapbox-gl';
import { X, Image, FrameCorners, ShoppingCart, Question } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import mapPreviewImg from '@/imports/OrderSelectSize/cb635bc29bbb01c1706f430f15fdc0d366601dea.png';
import suburbsPreviewImg from '../imports/suburbsPreview.png';
import streetsPreviewImg from '../imports/streetsPreview.png';
import { getRandomLocation } from './randomLocations';
import { savePendingOrder, takePendingOrder } from './pendingOrder';
import { ArPreviewModal } from './components/ArPreviewModal';
import { ArWallPreview } from './components/ArWallPreview';
import arWallTexture from '../imports/ar-wall-texture.png';

export interface SearchSuggestion {
  place_name: string;
  text: string;
  center: [number, number];
}

// Fixed reference images for the style picker's split preview — both styles
// use a static image here rather than rendering the current search location
// live (Suburbs can't render via Mapbox's Static Images API at all; Streets
// switched to match for visual consistency between the two options).
const STYLE_PREVIEW_IMAGES: Record<MapStyleKey, string> = {
  default: streetsPreviewImg,
  suburbs: suburbsPreviewImg,
};

export function SearchBar({ onSearch, onSelectSuggestion }: { onSearch: (query: string) => void; onSelectSuggestion: (suggestion: SearchSuggestion) => void }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const justSelected = useRef(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (justSelected.current) {
      justSelected.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxgl.accessToken}&limit=5`
        );
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          setSuggestions(data.features);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      onSearch(query);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    justSelected.current = true;
    setQuery(suggestion.text);
    setSuggestions([]);
    setShowSuggestions(false);
    onSelectSuggestion(suggestion);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full md:w-[552px]">
      <div className="bg-white content-stretch flex gap-[8px] items-center px-[24px] py-[12px] relative rounded-[9999px] shrink-0 w-full">
        <div aria-hidden="true" className="absolute border-2 border-[#404342] border-solid inset-0 pointer-events-none rounded-[9999px] shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]" />
        <div className="overflow-clip relative shrink-0 size-[24px]">
          <div className="absolute inset-[12.5%]">
            <div className="absolute inset-[-5.56%]">
              <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                <path d={svgPaths.p6e7ff00} stroke="var(--stroke-0, #404342)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
              </svg>
            </div>
          </div>
        </div>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="SEARCH LOCATION"
          className="font-['Roboto',sans-serif] font-normal leading-[normal] relative shrink-0 text-[#404342] text-[16px] tracking-[3.5px] uppercase bg-transparent border-none outline-none flex-1 placeholder:text-[#404342]"
          style={{ fontVariationSettings: "'wdth' 100" }}
        />
        {query && (
          <button
            onClick={handleClear}
            className="relative shrink-0 size-[24px] flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
            aria-label="Clear search"
          >
            <X size={16} weight="bold" className="text-[#404342]" />
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="bg-white content-stretch flex flex-col items-stretch absolute left-0 right-0 top-[calc(100%+4px)] rounded-[6px] z-10">
          <div aria-hidden="true" className="absolute border-2 border-[#404342] border-solid inset-0 pointer-events-none rounded-[6px] shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]" />
          {suggestions.map((suggestion, index) => (
            <div key={index}>
              <div
                className="relative shrink-0 w-full cursor-pointer hover:bg-[#f9fafb] transition-colors"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="flex flex-row items-center size-full">
                  <div className="content-stretch flex gap-[8px] items-center px-[24px] py-[12px] relative w-full">
                    <div className="overflow-clip relative shrink-0 size-[24px]">
                      <div className="absolute inset-[12.5%]">
                        <div className="absolute inset-[-5.56%]">
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <path d={svgPaths.p6e7ff00} stroke="var(--stroke-0, #404342)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <p className="font-['Roboto',sans-serif] font-normal leading-[normal] relative flex-1 min-w-0 text-[#404342] text-[16px] overflow-hidden text-ellipsis" style={{ fontVariationSettings: "'wdth' 100" }}>
                      {suggestion.place_name}
                    </p>
                  </div>
                </div>
              </div>
              {index < suggestions.length - 1 && (
                <div className="h-0 relative shrink-0 w-full">
                  <div className="absolute inset-[-1px_0_0_0]">
                    <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 552 1">
                      <line stroke="#E4E7EC" x2="552" y1="0.5" y2="0.5" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const SIZES = [
  { label: '55cm', price: '$179' },
  { label: '65cm', price: '$229' },
] as const;

type SubmitState = 'idle' | 'submitting' | 'error';

function OrderSheet({ open, onClose, location, labelName, address, coords, zoom, mapStyleKey, selectedSize, onSelectSize, initialCountry = null }: { open: boolean; onClose: () => void; location: string; labelName: string; address: string; coords: [number, number]; zoom: number; mapStyleKey: MapStyleKey; selectedSize: number | null; onSelectSize: (index: number) => void; initialCountry?: 'Australia' | 'New Zealand' | null }) {
  const [country, setCountry] = useState<'Australia' | 'New Zealand' | null>(initialCountry);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  // Reset the form whenever the sheet opens — to the provided initial
  // values if there are any (e.g. restoring after a cancelled checkout),
  // otherwise back to blank. Size is deliberately NOT reset here — it's
  // lifted to App level and shared with the size/AR sheet, so a choice
  // made there should still be reflected when this sheet is opened.
  useEffect(() => {
    if (open) {
      setCountry(initialCountry);
      setSubmitState('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const isValid = selectedSize !== null && country !== null;

  // Client-rendered map preview with the searched-location label baked in
  // (Suburbs style). Falls back to the Static Images API image if the render
  // fails, so the preview area always shows something.
  const previewFallbackUrl = getStaticMapPreviewUrl(coords, zoom, mapStyleKey, 1024, 1024);
  const { imageUrl: previewImageUrl } = useRenderedMapPreview(
    open,
    { coords, zoom, styleKey: mapStyleKey, label: labelName, badgeLabel: location, size: 2048 },
    previewFallbackUrl,
  );

  // Push the labelled preview to our backend, which forwards it to Stripe's
  // Files API and returns a hosted URL. That URL becomes the Stripe checkout
  // thumbnail and the image in both order emails. Best-effort: any failure
  // just means those surfaces fall back to the Static Images API preview.
  // Rendered fresh as JPEG (not the on-screen PNG) to stay under Stripe's
  // 512KB product-image limit. Hard-capped at 10s so a slow offscreen
  // render on a weak device can never hold up the redirect to payment.
  const uploadPreview = async (): Promise<string | undefined> => {
    const run = (async () => {
      const blob = await renderMapImage({
        coords,
        zoom,
        styleKey: mapStyleKey,
        label: labelName,
        badgeLabel: location,
        size: 1024,
        format: 'jpeg',
        quality: 0.9,
      });
      const form = new FormData();
      form.append('image', blob, 'preview.jpg');
      const res = await fetch(apiUrl('/upload-preview.php'), {
        method: 'POST',
        headers: { 'X-Order-Token': import.meta.env.VITE_ORDER_API_TOKEN ?? '' },
        body: form,
      });
      if (!res.ok) return undefined;
      const data = await res.json();
      return typeof data.url === 'string' ? data.url : undefined;
    })();

    const timeout = new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 10000));

    try {
      return await Promise.race([run, timeout]);
    } catch (err) {
      console.error('Preview upload failed; continuing without it:', err);
      return undefined;
    }
  };

  const handlePlaceOrder = async () => {
    if (!isValid || selectedSize === null || country === null) return;
    setSubmitState('submitting');

    const size = SIZES[selectedSize];

    // Persist the current order context so that if the customer cancels on
    // Stripe's page and lands back here, we can restore the map location
    // and pre-fill this form instead of making them start over. A full-page
    // redirect remounts the whole app, so this can't just live in React state.
    savePendingOrder({
      locationName: location,
      labelName,
      address,
      coords,
      zoom,
      sizeLabel: size.label,
      country,
      mapStyle: mapStyleKey,
    });

    try {
      // Render + upload the labelled preview before creating the session, so
      // its hosted URL can go straight onto the checkout page and into the
      // order emails. Undefined if anything failed — the backend falls back.
      const previewUrl = await uploadPreview();

      const response = await fetch(apiUrl('/create-checkout-session.php'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Order-Token': import.meta.env.VITE_ORDER_API_TOKEN ?? '',
        },
        body: JSON.stringify({
          locationName: location,
          labelName,
          address,
          lng: coords[0],
          lat: coords[1],
          zoom,
          deliveryCountry: country,
          sizeLabel: size.label,
          mapStyle: mapStyleKey,
          previewUrl,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'Request failed');

      // Full-page redirect to Stripe's hosted checkout — card details, email,
      // name, and shipping address are all entered there, never on this
      // site. The order confirmation email only ever fires after Stripe
      // confirms payment (see stripe-webhook.php), not from this redirect
      // itself.
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to start checkout:', error);
      setSubmitState('error');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            key="scrim"
            className="fixed inset-0 z-20 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Sheet */}
          <motion.aside
            key="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Order"
            className="fixed top-0 right-0 bottom-0 z-30 w-full max-w-[400px] bg-white flex flex-col overflow-hidden"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: [0.2, 0, 0, 1], duration: 0.35 }}
          >
            {/* Header */}
            <div className="relative shrink-0 w-full">
              <div aria-hidden className="absolute border-[#e4e7ec] border-b border-solid inset-0 pointer-events-none" />
              <div className="flex items-center justify-between pb-[21px] pt-[20px] px-[24px]">
                <p
                  className="font-['Roboto',sans-serif] font-normal text-[#404342] text-[16px] tracking-[3.5px] uppercase leading-[normal]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  Order
                </p>
                <button
                  onClick={onClose}
                  className="relative rounded-full shrink-0 size-[40px] flex items-center justify-center hover:bg-[#f5f5f5] transition-colors"
                  aria-label="Close"
                >
                  <svg className="size-[20px]" fill="none" viewBox="0 0 20 20">
                    <path d="M15 5L5 15" stroke="#404342" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                    <path d="M5 5L15 15" stroke="#404342" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div
              className="flex-1 overflow-y-auto p-[24px] flex flex-col gap-[24px]"
              style={{
                backgroundImage: `url(${arWallTexture})`,
                backgroundSize: '300px 300px',
                backgroundRepeat: 'repeat',
              }}
            >
              {/* Map preview — same framed-print-on-a-wall render as the
                  3D view, so what customers see here matches what they
                  already saw there rather than a flat map thumbnail. */}
                  <div className="relative shrink-0 w-full aspect-square">
                    {open && !previewImageUrl && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="size-[28px] rounded-full border-2 border-[#e4e7ec] border-t-[#404342] animate-spin" />
                      </div>
                    )}
                    {previewImageUrl && (
                    <ArWallPreview
                      active={open}
                      imageUrl={previewImageUrl}
                      sizeLabel={SIZES[selectedSize ?? 1].label}
                      location={location}
                      mapStyleKey={mapStyleKey}
                      // Taller than this wrapper's own reserved aspect-square
                      // footprint — see ArWallPreview.tsx for why. Only
                      // bleeds vertically (top/bottom); the sheet's width is
                      // fixed (no room to bleed sideways without triggering
                      // a horizontal scrollbar on the panel), and the
                      // model's own auto-framing already leaves comfortable
                      // horizontal margin for the drop-shadow at this width.
                      className="absolute -top-[40px] right-0 -bottom-[40px] left-0"
                    />
                    )}
                    {/* The Streets location badge used to be an HTML overlay
                        drawn only in this on-screen 3D view — see
                        locationBadge.ts, which now bakes the same-looking
                        badge into the map image itself (renderMapImage.ts),
                        so it shows on the checkout thumbnail, both order
                        emails, and the actual print too. Suburbs still needs
                        nothing here — its place name is baked into the map
                        as a big label instead (searchedLabel.ts). */}
                  </div>

                  {/* Size label */}
                  <p
                    className="font-['Roboto',sans-serif] font-normal text-[#404342] text-[16px] tracking-[3.5px] uppercase leading-[normal] shrink-0"
                    style={{ fontVariationSettings: "'wdth' 100" }}
                  >
                    Select map size
                  </p>

                  {/* Size cards */}
                  <div className="flex gap-[24px] shrink-0">
                    {SIZES.map((size, i) => {
                      const selected = selectedSize === i;
                      return (
                        <button
                          key={size.label}
                          onClick={() => onSelectSize(i)}
                          className={`relative rounded-[8px] flex-1 text-left transition-colors ${selected ? 'bg-[#404342]' : 'bg-white'}`}
                        >
                          <div aria-hidden className="absolute border-2 border-[#404342] border-solid inset-0 pointer-events-none rounded-[8px]" />
                          <div className={`flex flex-col items-start p-[24px] tracking-[3.5px] uppercase leading-[normal] ${selected ? 'text-white' : 'text-black'}`}>
                            <p
                              className="font-['Roboto',sans-serif] font-normal text-[16px] w-full"
                              style={{ fontVariationSettings: "'wdth' 100" }}
                            >
                              {size.label}
                            </p>
                            <p
                              className="font-['Playfair_Display',serif] font-extrabold text-[40px] w-full leading-tight"
                            >
                              {size.price}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Delivery country */}
                  <div className="flex flex-col gap-[8px] shrink-0">
                    <p className="font-['Roboto',sans-serif] font-normal text-[#6b7280] text-[16px] tracking-[1.5px] uppercase">
                      Delivery country
                    </p>
                    <div className="flex gap-[12px]">
                      {(['Australia', 'New Zealand'] as const).map((option) => {
                        const selected = country === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setCountry(option)}
                            className={`flex-1 h-[48px] flex items-center justify-center gap-[8px] rounded-[8px] border transition-colors ${selected ? 'border-[#404342] bg-[#404342]' : 'border-[#e4e7ec] bg-white'}`}
                          >
                            <span
                              aria-hidden
                              className={`size-[16px] rounded-full border-2 flex items-center justify-center ${selected ? 'border-white' : 'border-[#9ca3af]'}`}
                            >
                              {selected && <span className="size-[8px] rounded-full bg-white" />}
                            </span>
                            <span className={`font-['Roboto',sans-serif] font-normal text-[16px] ${selected ? 'text-white' : 'text-[#404342]'}`}>
                              {option}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {submitState === 'error' && (
                    <p className="font-['Roboto',sans-serif] font-normal text-[#dc2626] text-[16px] leading-relaxed">
                      Something went wrong starting checkout. Please try again.
                    </p>
                  )}
            </div>

            {/* Footer */}
              <div className="relative shrink-0 w-full">
                <div aria-hidden className="absolute border-[#e4e7ec] border-solid border-t inset-0 pointer-events-none" />
                <div className="px-[24px] pb-[20px] pt-[21px]">
                  <button
                    onClick={handlePlaceOrder}
                    disabled={!isValid || submitState === 'submitting'}
                    className="w-full h-[48px] flex items-center justify-center bg-[#404342] rounded-[9999px] transition-opacity disabled:opacity-40 cursor-pointer disabled:cursor-default"
                  >
                    <p className="font-['Roboto',sans-serif] font-normal text-white text-[16px] tracking-[3.5px] uppercase leading-[normal]"
                      style={{ fontVariationSettings: "'wdth' 100" }}
                    >
                      {submitState === 'submitting' ? 'Redirecting to payment…' : 'Continue to payment'}
                    </p>
                  </button>
                </div>
              </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function PostCheckoutBanner({ status, onClose }: { status: 'success' | 'cancelled' | null; onClose: () => void }) {
  return (
    <AnimatePresence>
      {status && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-[20px]"
          >
            <div className="relative bg-white rounded-[12px] max-w-[400px] w-full p-[32px] flex flex-col items-center text-center gap-[12px]">
              <button
                onClick={onClose}
                className="absolute top-[16px] right-[16px] cursor-pointer"
                aria-label="Close"
              >
                <X size={20} className="text-[#6b7280]" />
              </button>
              {status === 'success' ? (
                <>
                  <p className="font-['Playfair_Display',serif] font-extrabold text-[#404342] text-[28px] leading-tight">
                    Thanks for your order!
                  </p>
                  <p className="font-['Roboto',sans-serif] font-normal text-[#6b7280] text-[16px] tracking-[1px] leading-relaxed">
                    Your payment was successful. Check your email for a confirmation with all the details.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-['Playfair_Display',serif] font-extrabold text-[#404342] text-[28px] leading-tight">
                    Checkout cancelled
                  </p>
                  <p className="font-['Roboto',sans-serif] font-normal text-[#6b7280] text-[16px] tracking-[1px] leading-relaxed">
                    No payment was made. Feel free to try again whenever you're ready.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

type ActiveSheet = 'style' | 'cart' | null;

const ICON_BUTTON_BASE =
  'size-[48px] rounded-full border-2 border-[#404342] flex items-center justify-center transition-colors cursor-pointer';

// True on devices whose primary input is a real mouse (hover-capable,
// fine pointer). Drives IconNavButton's tooltip: hover-capable devices
// show it on mouseenter; touch devices never show it at all (there's no
// hover state to trigger it from) — tapping just activates immediately.
function useHasHoverCapability() {
  const query = '(hover: hover) and (pointer: fine)';
  const [hasHover, setHasHover] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handleChange = (e: MediaQueryListEvent) => setHasHover(e.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return hasHover;
}

// One IconNav icon, with its tooltip. Renders as a <button> (onActivate)
// or an <a> (href) — click/tap always activates immediately; the tooltip
// is purely a hover-capable-device affordance (mouseenter/mouseleave) and
// plays no part in touch interaction.
function IconNavButton({
  tooltip,
  active,
  Icon,
  hasHover,
  tooltipOpen,
  onShowTooltip,
  onHideTooltip,
  onActivate,
  href,
}: {
  tooltip: string;
  active: boolean;
  Icon: typeof Image;
  hasHover: boolean;
  tooltipOpen: boolean;
  onShowTooltip: () => void;
  onHideTooltip: () => void;
  onActivate: () => void;
  href?: string;
}) {
  const sharedProps = {
    'aria-label': tooltip,
    onMouseEnter: hasHover ? onShowTooltip : undefined,
    onMouseLeave: hasHover ? onHideTooltip : undefined,
    onClick: onActivate,
    className: `${ICON_BUTTON_BASE} ${active ? 'bg-[#e4e7eb]' : 'bg-white hover:bg-[#f9fafb]'}`,
  };

  return (
    <div className="relative">
      {tooltipOpen && (
        <div role="tooltip" className="absolute bottom-full left-1/2 -translate-x-1/2 mb-[10px] pointer-events-none z-20">
          <div className="bg-white border-2 border-[#404342] text-[#404342] text-[12px] font-['Roboto',sans-serif] font-normal tracking-[1.5px] uppercase rounded-[8px] px-[14px] py-[8px] whitespace-nowrap">
            {tooltip}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#404342]" />
          <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-[2px] w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[5px] border-t-white" />
        </div>
      )}
      {href ? (
        <a href={href} {...sharedProps}>
          <Icon size={22} weight="regular" className="text-[#404342]" />
        </a>
      ) : (
        <button type="button" aria-pressed={active} {...sharedProps}>
          <Icon size={22} weight="regular" className="text-[#404342]" />
        </button>
      )}
    </div>
  );
}

// The four persistent map-overlay icons. Style and cart each toggle their
// own small bottom-anchored sheet (StyleSheet / OrderSheet); AR has no
// sheet of its own — it jumps straight to the 3D view (see handleViewInAr).
// Only one sheet is ever open at a time; tapping the already-open one
// closes it. FAQs is a plain link out to the standalone /faq static page
// (frontend/public/faq/index.html) rather than a sheet — it has its own
// real content and layout, not something that fits the small sheet card.
function IconNav({ activeSheet, arOpen, onSelect }: { activeSheet: ActiveSheet; arOpen: boolean; onSelect: (sheet: 'style' | 'ar' | 'cart') => void }) {
  const items: { key: 'style' | 'ar' | 'cart'; tooltip: string; Icon: typeof Image }[] = [
    { key: 'style', tooltip: 'Styles', Icon: Image },
    { key: 'ar', tooltip: 'Preview', Icon: FrameCorners },
    { key: 'cart', tooltip: 'Order', Icon: ShoppingCart },
  ];

  const hasHover = useHasHoverCapability();
  const [tooltipKey, setTooltipKey] = useState<string | null>(null);

  return (
    <div data-icon-nav className="absolute bottom-[16px] left-0 right-0 md:bottom-[40px] z-10 flex justify-center gap-[12px]">
      {items.map(({ key, tooltip, Icon }) => {
        const active = key === 'ar' ? arOpen : activeSheet === key;
        return (
          <IconNavButton
            key={key}
            tooltip={tooltip}
            active={active}
            Icon={Icon}
            hasHover={hasHover}
            tooltipOpen={tooltipKey === key}
            onShowTooltip={() => setTooltipKey(key)}
            onHideTooltip={() => setTooltipKey((prev) => (prev === key ? null : prev))}
            onActivate={() => onSelect(key)}
          />
        );
      })}
      <IconNavButton
        tooltip="FAQs"
        active={false}
        Icon={Question}
        hasHover={hasHover}
        tooltipOpen={tooltipKey === 'faq'}
        onShowTooltip={() => setTooltipKey('faq')}
        onHideTooltip={() => setTooltipKey((prev) => (prev === 'faq' ? null : prev))}
        onActivate={() => {}}
        href="/faq/"
      />
    </div>
  );
}

// The style/size cards have no scrim (the map stays interactive around
// them), so clicking anywhere outside the open card — but not on the
// IconNav row itself, which has its own open/close toggle logic — is what
// makes them dismissible.
function useClickOutsideToClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Element;
      if (ref.current?.contains(target)) return;
      if (target.closest?.('[data-icon-nav]')) return;
      onClose();
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, onClose]);

  return ref;
}

// Shared small floating-card treatment for the style/size sheets — same
// pattern proven in ArTestApp.tsx's bottom panel. Sits just above IconNav;
// no scrim, the map stays visible and interactive around it.
const SHEET_CARD_CLASS =
  'fixed left-1/2 -translate-x-1/2 w-[calc(100%-32px)] md:w-[360px] bottom-[80px] md:bottom-[104px] z-10 bg-white rounded-[16px] p-[16px] flex flex-col gap-[16px]';
const SHEET_CARD_BORDER = 'absolute border-2 border-[#404342] border-solid inset-0 pointer-events-none rounded-[16px] shadow-[0px_20px_24px_0px_rgba(16,24,40,0.08),0px_8px_8px_0px_rgba(16,24,40,0.03)]';

function StyleSheet({ open, mapStyleKey, onSelectStyle, onClose }: { open: boolean; mapStyleKey: MapStyleKey; onSelectStyle: (key: MapStyleKey) => void; onClose: () => void }) {
  const cardRef = useClickOutsideToClose(open, onClose);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={cardRef}
          key="style-sheet"
          className={SHEET_CARD_CLASS}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.2 }}
        >
          <div aria-hidden className={SHEET_CARD_BORDER} />
          <div className="relative flex gap-[12px]">
            {(Object.keys(MAP_STYLES) as MapStyleKey[]).map((key) => {
              const selected = key === mapStyleKey;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectStyle(key)}
                  aria-label={MAP_STYLES[key].label}
                  aria-pressed={selected}
                  className="relative flex-1 aspect-square rounded-[8px] overflow-hidden cursor-pointer group"
                >
                  {/* Fixed reference images, not the live location — Suburbs
                      doesn't render via Mapbox's Static Images API at all;
                      see the note on STYLE_PREVIEW_IMAGES above. */}
                  <img
                    src={STYLE_PREVIEW_IMAGES[key]}
                    alt={`${MAP_STYLES[key].label} style`}
                    className="absolute inset-0 size-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div
                    className={`absolute inset-0 border-2 pointer-events-none transition-colors rounded-[8px] ${
                      selected ? 'border-[#404342]' : 'border-transparent'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function App() {
  // Detect returning from Stripe Checkout before anything else renders, so
  // we can restore the right starting location/form state on the very
  // first paint rather than flashing a random location first.
  const [initialOrderStatus] = useState<'success' | 'cancelled' | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const order = params.get('order');
    return order === 'success' || order === 'cancelled' ? order : null;
  });
  // Lets the homepage's two style preview images link straight into the
  // matching style (?style=default / ?style=suburbs) instead of always
  // landing on the default style.
  const [initialStyleParam] = useState<MapStyleKey | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const style = params.get('style');
    return style === 'default' || style === 'suburbs' ? style : null;
  });
  // Consumed (and removed from sessionStorage) regardless of success or
  // cancelled, so it never lingers into a later, unrelated visit — but only
  // actually used to restore anything on the cancelled path below.
  const [pendingOrderData] = useState(() => (initialOrderStatus ? takePendingOrder() : null));

  const [initialLocation] = useState(() => {
    if (pendingOrderData) {
      return { name: pendingOrderData.locationName, address: pendingOrderData.address, coords: pendingOrderData.coords };
    }
    return getRandomLocation();
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{ coords: [number, number]; name: string; address: string; token: number } | null>(null);
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(
    initialOrderStatus === 'cancelled' && pendingOrderData !== null ? 'cart' : null
  );
  const [previewLocation, setPreviewLocation] = useState(initialLocation.name);
  // The searched/landing place name — drives the big rendered label, kept
  // separate from previewLocation (the order text / Streets badge, which
  // follows pan/zoom reverse-geocoding) so the label always matches the
  // live map.
  const [previewLabelName, setPreviewLabelName] = useState(pendingOrderData?.labelName || initialLocation.name);
  const [previewAddress, setPreviewAddress] = useState(initialLocation.address);
  const [previewCoords, setPreviewCoords] = useState<[number, number]>(initialLocation.coords);
  const [previewZoom, setPreviewZoom] = useState(pendingOrderData?.zoom ?? getDefaultZoom());
  const [postCheckoutStatus, setPostCheckoutStatus] = useState<'success' | 'cancelled' | null>(null);
  const [mapStyleKey, setMapStyleKey] = useState<MapStyleKey>(pendingOrderData?.mapStyle ?? initialStyleParam ?? 'default');
  const restoredSizeIndexAtMount = pendingOrderData
    ? SIZES.findIndex((s) => s.label === pendingOrderData.sizeLabel)
    : -1;
  const [selectedSize, setSelectedSize] = useState<number | null>(
    restoredSizeIndexAtMount >= 0 ? restoredSizeIndexAtMount : null
  );
  const [arOpen, setArOpen] = useState(false);
  const [arLocation, setArLocation] = useState('');
  // The searched/landing place name — what the big label shows. Kept apart
  // from arLocation (the possibly pan-drifted reverse-geocoded name) so the
  // rendered preview always matches the live map's label.
  const [arLabelName, setArLabelName] = useState('');
  const [arMapStyleKey, setArMapStyleKey] = useState<MapStyleKey>('default');
  // Coords/zoom snapshotted when "View in AR" is tapped — the AR map image is
  // rendered from these (see the useRenderedMapPreview call below).
  const [arCoords, setArCoords] = useState<[number, number]>(initialLocation.coords);
  const [arZoom, setArZoom] = useState(pendingOrderData?.zoom ?? getDefaultZoom());
  const locationRef = useRef<MapLocation>({
    coords: initialLocation.coords,
    zoom: pendingOrderData?.zoom ?? getDefaultZoom(),
    name: initialLocation.name,
    address: initialLocation.address,
    searchedName: pendingOrderData?.labelName || initialLocation.name,
  });

  // Clean the ?order=... URL immediately so refreshing doesn't re-trigger
  // anything. Show the generic banner for 'success', or for 'cancelled'
  // only when there was no pending order to restore into the sheet instead
  // (showing both the banner and the reopened form would be redundant).
  useEffect(() => {
    if (initialOrderStatus) {
      const showGenericBanner = initialOrderStatus === 'success' || !pendingOrderData;
      if (showGenericBanner) {
        setPostCheckoutStatus(initialOrderStatus);
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('order');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
    if (initialStyleParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete('style');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    // token guarantees the map effect re-fires even if the same suggestion is clicked twice in a row
    setSelectedLocation({ coords: suggestion.center, name: suggestion.text, address: suggestion.place_name, token: Date.now() });
  };

  const handleLocationSelect = useCallback((locationName: string) => {
    locationRef.current.name = locationName;
  }, []);

  // Runs when the cart icon opens the order sheet — mirrors what the old
  // top "Order" button used to do on click.
  const handleCartOpen = () => {
    setPreviewLocation(locationRef.current.name);
    setPreviewLabelName(locationRef.current.searchedName);
    setPreviewAddress(locationRef.current.address);
    setPreviewCoords(locationRef.current.coords);
    setPreviewZoom(locationRef.current.zoom);

    // Notify the team by email — fire and forget, doesn't block opening the sheet.
    const { coords, zoom, name, address } = locationRef.current;
    fetch(apiUrl('/send-order-email.php'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Order-Token': import.meta.env.VITE_ORDER_API_TOKEN ?? '',
      },
      body: JSON.stringify({
        locationName: name,
        address,
        lng: coords[0],
        lat: coords[1],
        zoom,
        mapStyle: mapStyleKey,
      }),
    }).catch((error) => {
      console.error('Failed to send order notification email:', error);
    });
  };

  const handleIconSelect = (sheet: 'style' | 'ar' | 'cart') => {
    if (sheet === 'ar') {
      handleViewInAr();
      return;
    }
    const opening = activeSheet !== sheet;
    setActiveSheet(opening ? sheet : null);
    if (opening && sheet === 'cart') {
      handleCartOpen();
    }
  };

  // Snapshot the ref's current coords into state right when the button is
  // clicked, rather than reading locationRef during render (which would be
  // an unreliable, non-reactive read) — same pattern proven in
  // ArTestApp.tsx's handleViewInAr. No size-picking step before this any
  // more — jumps straight to the 3D view, defaulting to 65cm (index 1) the
  // first time, or whatever size is already selected (e.g. from the order
  // sheet) after that.
  const handleViewInAr = () => {
    if (selectedSize === null) setSelectedSize(1);
    const { coords, zoom, name, searchedName } = locationRef.current;
    setArCoords(coords);
    setArZoom(zoom);
    setArLocation(name);
    setArLabelName(searchedName);
    setArMapStyleKey(mapStyleKey);
    setArOpen(true);
  };

  // "Order" inside the 3D view hands off to the same cart flow the cart
  // icon opens — size carries over since both read/write the shared
  // selectedSize state, so there's nothing left to re-pick.
  const handleOrderFromAr = () => {
    setArOpen(false);
    setActiveSheet('cart');
    handleCartOpen();
  };

  // Map image for the AR model texture — same client render as the Order
  // sheet, searched-location label baked in, Static Images API as fallback.
  const arPreviewFallbackUrl = getStaticMapPreviewUrl(arCoords, arZoom, arMapStyleKey, 1024, 1024);
  const { imageUrl: arImageUrl } = useRenderedMapPreview(
    arOpen,
    { coords: arCoords, zoom: arZoom, styleKey: arMapStyleKey, label: arLabelName, badgeLabel: arLocation, size: 2048 },
    arPreviewFallbackUrl,
  );

  return (
    <div className="relative size-full">
      <MapboxMap onLocationSelect={handleLocationSelect} searchQuery={searchQuery} selectedLocation={selectedLocation} initialLocation={initialLocation} mapStyleKey={mapStyleKey} className="absolute inset-0 z-0" locationRef={locationRef} />
      <div className="absolute top-[16px] left-[16px] right-[16px] md:top-[40px] md:left-1/2 md:-translate-x-1/2 md:right-auto z-10">
        <SearchBar onSearch={handleSearch} onSelectSuggestion={handleSelectSuggestion} />
      </div>

      <IconNav activeSheet={activeSheet} arOpen={arOpen} onSelect={handleIconSelect} />

      <StyleSheet
        open={activeSheet === 'style'}
        mapStyleKey={mapStyleKey}
        onSelectStyle={setMapStyleKey}
        onClose={() => setActiveSheet(null)}
      />

      <OrderSheet
        open={activeSheet === 'cart'}
        onClose={() => setActiveSheet(null)}
        location={previewLocation}
        labelName={previewLabelName}
        address={previewAddress}
        coords={previewCoords}
        zoom={previewZoom}
        mapStyleKey={mapStyleKey}
        selectedSize={selectedSize}
        onSelectSize={setSelectedSize}
        initialCountry={pendingOrderData?.country ?? null}
      />

      <ArPreviewModal
        open={arOpen}
        onClose={() => setArOpen(false)}
        imageUrl={arImageUrl}
        sizeOptions={SIZES}
        selectedSizeIndex={selectedSize ?? 1}
        onSelectSize={setSelectedSize}
        onOrder={handleOrderFromAr}
        location={arLocation}
        mapStyleKey={arMapStyleKey}
      />

      <PostCheckoutBanner status={postCheckoutStatus} onClose={() => setPostCheckoutStatus(null)} />

      {TEST_MODE && (
        <div className="fixed bottom-[12px] left-[12px] z-50 rounded-full bg-[#dc2626] px-[12px] py-[6px] font-['Roboto',sans-serif] text-[11px] font-normal uppercase tracking-[0.15em] text-white shadow-[0px_8px_16px_rgba(0,0,0,0.2)] pointer-events-none">
          Test mode
        </div>
      )}
    </div>
  );
}